<script>
    import Select from "../../../inputs/Select/v1/Select.svelte";
    import NextPage from "./NextPage.svelte";
    import PreviousPage from "./PreviousPage.svelte";
    import { guidV4 } from '@itslearning/atlas/generate/guid';
    import { formatLanguageString } from '@itslearning/atlas/strings/format';
    import { createEventDispatcher, tick } from 'svelte';

    /** @type {'polite'|'assertive'|'off'} */
    export let ariaLiveUpdateType = 'polite';

    /** @type {number} */
    export let currentPage = 1;

    export let i18n = {};

    /** @type {number} */
    export let pageSize = 25;

    /** @type {boolean} */
    export let showCompactView = false;

    /** @type {boolean} */
    export let showNumberOfItems = false;

    /** @type {boolean} */
    export let showPageSize = true;

    /** @type {boolean} */
    export let singlePageApp = false;

    /** @type {number} */
    export let totalCount = 0;

    /** @type {boolean} */
    export let showAllPageNumbers = false;

    /** @type {number[]} */
    export let pageSizesList = [10, 25, 50, 100];

    const dispatch = createEventDispatcher();

    let pages = [];
    const firstPage = 1;
    const ellipsis = -1;
    const id = guidV4();

    let compactViewText = '';
    let pageSizesText = '';

    const originalSizesList = pageSizesList;

    $: {
        pageSizesList = [...new Set([...originalSizesList, pageSize])].sort((a, b) => a - b);
    }

    $: compactViewText = formatLanguageString(i18n.compactText, currentPage.toString(), lastPageNumber.toString());
    $: pageSizesText = showNumberOfItems
        ? formatLanguageString(i18n.pageSizes, currentPageItemsCount.toString(), exactPageItemsCount.toString(), totalCount.toString())
        : '';

    $: lastPageNumber = Math.ceil(totalCount / pageSize);
    $: pageCount = totalCount <= pageSize ? 1 : (totalCount % pageSize > 0 ? lastPageNumber : lastPageNumber);
    $: exactPageItemsCount = pageSize * currentPage < totalCount ? pageSize * currentPage : totalCount;
    $: currentPageItemsCount = (currentPage - 1) * pageSize + 1;

    async function pageClickHandler(event, page) {
        if (singlePageApp) {
            event.preventDefault();
            
            if (currentPage !== page) {
                currentPage = page;
            }

            const linkElement = event.target.closest('.prom-pagination__list');

            await tick();
            linkElement.querySelector(".prom-pagination__link[aria-current]").focus();
        }
        dispatch("pageClick", page);
    }

    function handlePreviousPage(event) {
        currentPage = event.detail.page;
        dispatch("previousPage", event.detail.page);
    }

    function handleNextPage(event) {
        currentPage = event.detail.page;
        dispatch("nextPage", event.detail.page);
    }

    $: {
        if (pageCount > 1 && currentPage > 0 && currentPage <= lastPageNumber) {
            pages = [];
    
            if (showAllPageNumbers) {
                for (let startPage = 1; startPage <= pageCount; startPage++) {
                    pages.push(startPage);
                }
            } else {
                for (let startPage = 1; startPage <= pageCount; startPage++) {
                    if (startPage === currentPage) {
                        if (currentPage === firstPage){
                            pages.push(firstPage);
                            if (startPage + 1 < lastPageNumber) {
                                pages.push(startPage + 1);
                                if (startPage + 2 <= lastPageNumber) {
                                    if (startPage + 3 < lastPageNumber) {
                                        pages.push(ellipsis);
                                    } else {
                                        pages.push(startPage + 2);
                                    }
                                }
                            }
                        } else if (startPage === lastPageNumber) {
                            pages.push(currentPage);
                            continue;
                        } else if (startPage === (lastPageNumber - 1)) {
                            pages.push(currentPage);
                        } else if (startPage === (lastPageNumber - 2)) {
                            pages.push(currentPage);
                            pages.push(currentPage + 1);
                        } else if (startPage === (lastPageNumber - 3)) {
                            pages.push(currentPage);
                            pages.push(currentPage + 1);
                            pages.push(currentPage + 2);
                        } else {
                            pages.push(currentPage);
                            pages.push(currentPage + 1);
                            pages.push(ellipsis);
                        }
    
                        if (!pages.includes(lastPageNumber)) {
                            pages.push(lastPageNumber);
                        }
                    } else {
                        if (startPage === firstPage) {
                            pages.push(firstPage);
                        } else if (startPage === (currentPage - 1)) {
                            if (startPage >= (firstPage + 1)) {
                                if (startPage - firstPage === 1) {
                                    pages.push(startPage);
                                } else {
                                    if ((currentPage - 2) - firstPage === 1) {
                                        pages.push(startPage - 1);
                                    } else {
                                        pages.push(ellipsis);
                                    }
                                    pages.push(startPage);
                                }
                            } else {
                                pages.push(startPage);
                            }
                        }
                    }
                }
            }
        } else {
            pages = [1];
        }
    }
</script>

{#if pageCount > 1 || totalCount >= Math.min(...pageSizesList)}
    <div class="prom-pagination__wrapper {showCompactView ? "prom-pagination__compact-view" : ""}">
        <nav aria-label="pagination" class="prom-pagination pagecount-{pageCount} current-{currentPage} last-{lastPageNumber}">
            <ul class="prom-pagination__list">
                <PreviousPage {i18n} {currentPage} {singlePageApp} {showCompactView} on:previousPage={handlePreviousPage} />

                {#if showCompactView}
                    <li class="prom-pagination__compact">{compactViewText}</li>
                {:else}
                    {#each pages as page}
                        <li class="prom-pagination__listitem">
                            {#if page === ellipsis}
                                <span>…</span>
                            {:else}
                                <a
                                    class="prom-pagination__link"
                                    href="/?page={page}"
                                    aria-label={formatLanguageString(i18n.pageX, page)}
                                    aria-current={page === currentPage ? "page" : undefined}
                                    on:click={(event) => pageClickHandler(event, page)}>{page}</a>
                            {/if}
                        </li>
                    {/each}
                {/if}

                <NextPage {i18n} {currentPage} {lastPageNumber} {singlePageApp} {showCompactView} on:nextPage={handleNextPage} />
            </ul>

            <span aria-live={ariaLiveUpdateType} class="screen-reader">{compactViewText + ", " + pageSizesText}</span>
        </nav>

        <div class="prom-pagination__page-size-container">
            <div class="prom-pagination__page-size">
                {#if showNumberOfItems}
                    <span class="prom-pagination__page-size-text" id="prom-pagination__page-size-select_{id}">{pageSizesText}</span>
                {/if}
        
                {#if showPageSize}
                    <Select
                        bind:value={pageSize}
                        describedBy="prom-pagination__page-size-select_{id}"
                        label={i18n.sizeSelectLabel}
                        on:change={() => currentPage = 1}
                        on:change={() => dispatch("pageSizeChange")}
                        labelHidden
                    >
                        {#each pageSizesList as size}
                            <option value={size}>{size}</option>
                        {/each}
                    </Select>
                {/if}
            </div>
        </div>
    </div>
{/if}
